/**
 * Format bytes into a human-readable string (e.g., "2.4 MB").
 */
export const formatFileSize = (bytes: number | null): string => {
  if (bytes === null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let unitIndex = 0
  let size = bytes
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(size < 10 && unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`
}

/**
 * Extract the file extension from a filename.
 */
export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.')
  return parts.length > 1 ? parts.pop()!.toLowerCase() : ''
}

/**
 * Map a MIME type to a display-friendly category label.
 */
export const getMimeCategory = (mimeType: string | null): string => {
  if (!mimeType) return 'folder'
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType === 'text/csv') return 'spreadsheet'
  return 'file'
}

/**
 * Allowed MIME types for upload validation (client-side).
 */
export const ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'video/mp4',
  'text/csv',
]

/**
 * Allowed extensions for the file input accept attribute.
 */
export const ACCEPTED_FILE_EXTENSIONS = '.png,.jpg,.jpeg,.mp4,.csv'

/**
 * Max upload sizes in bytes, keyed by MIME category prefix.
 */
export const MAX_UPLOAD_SIZES: Record<string, number> = {
  image: 5 * 1024 * 1024,    // 5 MB
  text: 5 * 1024 * 1024,     // 5 MB
  video: 25 * 1024 * 1024,   // 25 MB
}

/**
 * Validate a file before upload. Returns an error i18n key or null.
 */
export const validateFile = (file: File): string | null => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return 'fileRepository.modal.upload.error.unsupportedType'
  }
  const category = file.type.split('/')[0] // 'image', 'video', 'text'
  const maxSize = MAX_UPLOAD_SIZES[category]
  if (maxSize && file.size > maxSize) {
    return 'fileRepository.modal.upload.error.fileTooLarge'
  }
  return null
}

/**
 * Get the max size in MB for a given file (for error messages).
 */
export const getMaxSizeMB = (file: File): number => {
  const category = file.type.split('/')[0]
  const maxBytes = MAX_UPLOAD_SIZES[category] || 5 * 1024 * 1024
  return maxBytes / (1024 * 1024)
}

/**
 * Format an ISO date string to a short display format.
 */
export const formatShortDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
